import { FormattedPrice, RichTextValue } from "@msys/ui";
import { Box, Card, CardActionArea, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import React from "react";
import { WizardQuestions_Question_WizardDecisionQuestion_Fragment } from "../../documents/WizardQuestions.generated.js";
import { decisionAction } from "./answerToItemAction.js";
import { WizardQuestionMedia } from "./WizardQuestionMedia.js";
import {
  WizardRendererProps,
  WizardRendererRef,
} from "./WizardRendererProps.js";

/**
 * A decision, rendered from the question's own options: `one` replaces the
 * selection and answers at once, `many` toggles it and stays staged until the
 * action bar's forward button submits through the ref. The preselection the
 * question reports is the starting state.
 */
export const WizardDecisionRenderer = React.forwardRef<
  WizardRendererRef,
  WizardRendererProps<WizardQuestions_Question_WizardDecisionQuestion_Fragment>
>(({ question, onAnswer, disabled }, ref) => {
  const preselected = React.useMemo(
    () =>
      question.options.filter(o => o.preselected).map(option => option.itemId),
    [question.options]
  );
  const [selected, setSelected] = React.useState<Array<string>>(preselected);

  React.useEffect(() => setSelected(preselected), [preselected]);

  const toggle = (itemId: string) => {
    const next =
      question.behavior === "one"
        ? [itemId]
        : selected.includes(itemId)
          ? selected.filter(id => id !== itemId)
          : [...selected, itemId];

    setSelected(next);
    if (question.behavior === "one") {
      void onAnswer(decisionAction(question.itemId, next));
    }
  };

  React.useImperativeHandle(
    ref,
    () => ({
      submit: () => void onAnswer(decisionAction(question.itemId, selected)),
    }),
    [onAnswer, question.itemId, selected]
  );

  return (
    <Stack direction="column" spacing={1}>
      {question.options.map(option => {
        const isSelected = selected.includes(option.itemId);

        return (
          <Card key={option.itemId} variant="outlined">
            <CardActionArea
              disabled={disabled}
              onClick={() => toggle(option.itemId)}
              aria-pressed={isSelected}
              sx={{ p: 1.5 }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ alignItems: "center" }}
              >
                {isSelected ? (
                  <CheckCircleIcon color="primary" />
                ) : (
                  <RadioButtonUncheckedIcon color="disabled" />
                )}
                <WizardQuestionMedia media={option.media} />
                <Stack direction="column" sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1">{option.title}</Typography>
                  {option.description && (
                    // Descriptions are rich-text HTML: RichTextValue renders it
                    // without raw innerHTML, and the clamp keeps the card compact
                    // (TruncatedHtmlValue's button would nest in the card button).
                    <Box
                      sx={{
                        typography: "body2",
                        color: "text.secondary",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      <RichTextValue htmlContent={option.description} />
                    </Box>
                  )}
                </Stack>
                {option.price !== null && option.price !== undefined && (
                  <Typography variant="body2">
                    <FormattedPrice
                      value={{
                        amount: option.price,
                        currency: option.currency,
                      }}
                    />
                  </Typography>
                )}
              </Stack>
            </CardActionArea>
          </Card>
        );
      })}
    </Stack>
  );
});
